import { SelectV2, Text } from '@cimplico/components';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CSSProperties, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { EntityAccount } from '../../types/entity';
import { AccountMapping, MappingFormData } from './AccountsMappingForm';

type AccountMapRowProps = {
    mapping: AccountMapping;
    entityAccounts: EntityAccount[];
    idx: number;
    usedMappings: Set<string>;
    style?: CSSProperties;
};

function AccountMapRow({ mapping, entityAccounts, idx, usedMappings, style }: AccountMapRowProps): JSX.Element | null {
    const account = mapping.account;

    return (
        <div
            className={`grid grid-cols-[400px_100px_450px] gap-x-6 border-b last:border-0 items-center py-2 px-4 ${
                mapping?.entityAccount ? '' : 'bg-red-50'
            }`}
            style={style}
        >
            <Text className={`${mapping?.entityAccount ? ' text-gray-600' : 'text-red-600'} text-sm justify-self-start`} lines={2}>
                {account.code ? `${account.code} - ${account.description}` : account.description}
            </Text>
            <div className="flex justify-center">
                <ArrowRightIcon className={`h-5 w-5 ${mapping?.entityAccount ? 'text-indigo-600' : 'text-red-600'}`} />
            </div>
            <div className="flex items-center gap-1 justify-between pr-6">
                <EntityAccountSelect entityAccounts={entityAccounts} idx={idx} mapping={mapping} usedMappings={usedMappings} />
                <div className="h-5 w-5">{!mapping?.entityAccount && <ExclamationCircleIcon className="h-5 w-5 text-red-600" />} </div>
            </div>
        </div>
    );
}

function EntityAccountSelect({ entityAccounts, mapping, idx, usedMappings }: AccountMapRowProps): JSX.Element {
    const context = useFormContext<MappingFormData>();

    const bestMatch = useMemo(() => {
        return !mapping.entityAccount
            ? entityAccounts.find((ea) =>
                  mapping.account?.code ? mapping.account.code === ea.code : mapping.account.description === ea.name
              )
            : undefined;
    }, [entityAccounts, mapping.account.code, mapping.account.description, mapping.entityAccount]);

    const defaultOptions = useMemo(() => {
        return entityAccounts.map((ea) => {
            const label = ea.code ? `${ea.code} - ${ea.name}` : `${ea.name}`;
            return {
                label,
                value: ea.id,
                isDisabled: usedMappings.has(ea.id),
                disabledHint: 'This account has already been mapped.',
            };
        });
    }, [entityAccounts, usedMappings]);

    const options = useMemo(() => {
        return bestMatch
            ? [
                  {
                      label: bestMatch.code ? `${bestMatch.code} - ${bestMatch.name}` : `${bestMatch.name}`,
                      value: bestMatch.id,
                  },
                  ...defaultOptions.filter((ea) => ea.value !== bestMatch.id),
              ]
            : defaultOptions;
    }, [bestMatch, defaultOptions]);

    return (
        <div className="w-full">
            <Controller
                control={context.control}
                name={`mappings.${idx}`}
                render={({ field }): JSX.Element => {
                    return (
                        <SelectV2
                            disabledHint={`This account has already been mapped.`}
                            disabled={Boolean(field.value.originalEntityAccount)}
                            placeholder="Select an account..."
                            className="w-full"
                            options={options}
                            value={
                                mapping?.entityAccount
                                    ? [
                                          {
                                              label: mapping.entityAccount.code
                                                  ? `${mapping.entityAccount.code} - ${mapping.entityAccount.name}`
                                                  : `${mapping.entityAccount.name}`,
                                              value: mapping.entityAccount.id,
                                          },
                                      ]
                                    : []
                            }
                            onChange={(v) => {
                                if (v.length !== 0) {
                                    const val = v[0];
                                    const id = val.value;
                                    const ea = entityAccounts.find((ea) => ea.id === id);
                                    field.onChange({
                                        account: mapping.account,
                                        entityAccount: ea,
                                        index: idx,
                                        originalEntityAccount: mapping.originalEntityAccount,
                                    });
                                } else {
                                    field.onChange({
                                        account: mapping.account,
                                        entityAccount: undefined,
                                        index: idx,
                                        originalEntityAccount: mapping.originalEntityAccount,
                                    });
                                }
                            }}
                            size="small"
                        />
                    );
                }}
            />
        </div>
    );
}

export default AccountMapRow;
